import axios from 'axios';
import i18n from 'i18next';
import { buildApiUrl } from '../config/apiConfig';
import {
  isAlreadyRedeemedGiftError,
  normalizeGiftCode,
  peekPendingGiftCode,
  clearPendingGiftCode,
} from './giftVoucherCode';
import { giftChrome, localizeGiftApiError, giftErrorFromUnknown } from '../pages/GiftVoucherPage/giftExperience';

export type GiftClaimResult = {
  product_type: string;
  product_name?: string;
  message?: string;
  already_owned?: boolean;
  client_view?: Record<string, unknown>;
};

export function giftClaimProSlug(result: Pick<GiftClaimResult, 'client_view'>): string | null {
  const pro = result.client_view?.pro;
  if (!pro || typeof pro !== 'object') return null;
  const slug = (pro as { slug?: unknown }).slug;
  return typeof slug === 'string' && slug.trim() ? slug.trim() : null;
}

type GiftClaimListener = (result: GiftClaimResult) => void;
const giftClaimListeners = new Set<GiftClaimListener>();

export function subscribeGiftClaim(listener: GiftClaimListener): () => void {
  giftClaimListeners.add(listener);
  return () => {
    giftClaimListeners.delete(listener);
  };
}

function emitGiftClaim(result: GiftClaimResult): GiftClaimResult {
  giftClaimListeners.forEach(listener => listener(result));
  return result;
}

export type GiftPaymentApply = 'forfait' | 'credits' | 'none';

/** Forfait déjà sur le compte, sinon crédits suffisants — sans nouveau claim. */
export function preferredWalletPaymentMode(opts: {
  hasValidForfait: boolean;
  credits: number;
  creditPrice: number;
}): GiftPaymentApply {
  if (opts.hasValidForfait) return 'forfait';
  if (opts.creditPrice > 0 && opts.credits >= opts.creditPrice) return 'credits';
  return 'none';
}

/** Un cadeau sur le compte n’est « inclus » que s’il couvre vraiment ce service. */
export function resolveGiftPaymentApply(
  result: Pick<GiftClaimResult, 'product_type'>,
  opts: { credits: number; hasValidForfait: boolean; creditPrice: number }
): GiftPaymentApply {
  if (result.product_type === 'credit_pack') {
    return preferredWalletPaymentMode({ ...opts, hasValidForfait: false });
  }
  return preferredWalletPaymentMode({ ...opts, credits: 0, creditPrice: 0 });
}

export function giftClaimAppliesToService(
  result: Pick<GiftClaimResult, 'product_type'>,
  apply: GiftPaymentApply
): boolean {
  if (result.product_type === 'credit_pack') return true;
  return apply === 'forfait';
}

function apiErrorMessage(err: unknown): string {
  const e = err as { response?: { data?: { error?: string } } };
  return e.response?.data?.error || '';
}

function alreadyOwnedView(data: {
  status?: string;
  viewer_role?: string;
  product_type?: string;
  product_name?: string;
}): GiftClaimResult | null {
  if (data.status !== 'redeemed' || data.viewer_role !== 'recipient') {
    return null;
  }
  return {
    product_type: data.product_type || '',
    product_name: data.product_name,
    message: giftChrome(i18n.language).alreadyOnAccount,
    already_owned: true,
    client_view: data,
  };
}

export async function claimGiftCode(
  rawCode: string,
  token: string
): Promise<GiftClaimResult> {
  const code = normalizeGiftCode(rawCode);
  if (!code) {
    throw new Error(giftChrome(i18n.language).missingCode);
  }
  const headers = { Authorization: `Bearer ${token}` };
  try {
    const res = await axios.post(
      buildApiUrl(`/public/gift-vouchers/${encodeURIComponent(code)}/redeem`),
      {},
      { headers }
    );
    return emitGiftClaim({
      product_type: res.data?.product_type || '',
      product_name: res.data?.product_name,
      message: res.data?.message,
      already_owned: Boolean(res.data?.already_owned),
      client_view: res.data?.client_view,
    });
  } catch (err: unknown) {
    const message = apiErrorMessage(err);
    if (!isAlreadyRedeemedGiftError(message)) {
      throw err;
    }
    try {
      const view = await axios.get(
        buildApiUrl(`/public/gift-vouchers/${encodeURIComponent(code)}`),
        { headers }
      );
      const owned = alreadyOwnedView(view.data || {});
      if (owned) return emitGiftClaim(owned);
    } catch {
      // Garder l’erreur de redeem d’origine.
    }
    throw err;
  }
}

let giftAutoClaimLock = false;

export function startPendingGiftAutoClaim(
  token: string | null | undefined,
  handlers: {
    onSuccess: (result: GiftClaimResult) => void | Promise<void>;
    onError?: (message: string) => void;
    onSettled?: () => void;
  }
): boolean {
  if (!token || giftAutoClaimLock) return false;
  const pending = peekPendingGiftCode();
  if (!pending) return false;
  giftAutoClaimLock = true;
  void claimGiftCode(pending, token)
    .then(async result => {
      clearPendingGiftCode();
      await handlers.onSuccess(result);
    })
    .catch((err: unknown) => {
      handlers.onError?.(
        localizeGiftApiError(giftErrorFromUnknown(err), i18n.language)
      );
    })
    .finally(() => {
      giftAutoClaimLock = false;
      handlers.onSettled?.();
    });
  return true;
}
